/**
 * Usage Tracking Hook
 * 
 * React hook for managing usage data, credit information, and usage-related state.
 * Provides real-time usage tracking and automatic refresh capabilities.
 */

import { useState, useEffect, useCallback } from 'react';
import { usageService } from '../services/usageService';
import { debugLog, debugError } from '@/lib/debugUtils';
import type {
  UsageSummary,
  CreditTransaction,
  UseUsageTrackingResult,
  UsageServiceError
} from '../types/usage';

/**
 * Hook configuration options
 */
interface UseUsageTrackingConfig {
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
  enableRealTimeUpdates?: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: UseUsageTrackingConfig = {
  autoRefresh: true,
  refreshInterval: 30000, // 30 seconds
  enableRealTimeUpdates: false // Can be enabled for real-time features later
};

/**
 * Custom hook for usage tracking and management
 * 
 * @param userId - The user ID to track usage for
 * @param config - Optional configuration for the hook behavior
 * @returns Usage tracking state and methods
 */
export function useUsageTracking(
  userId: string,
  config: UseUsageTrackingConfig = DEFAULT_CONFIG
): UseUsageTrackingResult {
  // ============================================================================
  // State Management
  // ============================================================================
  
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // ============================================================================
  // Core Functions
  // ============================================================================

  /**
   * Refresh usage summary from the server
   */
  const refreshUsage = useCallback(async (): Promise<void> => {
    if (!userId) {
      debugLog('useUsageTracking: No userId provided, skipping refresh');
      return;
    }

    try {
      debugLog('useUsageTracking: Refreshing usage summary', { userId });
      
      const summary = await usageService.getUsageSummary(userId);
      setUsageSummary(summary);
      setError(null);
      setLastRefresh(new Date());
      
      debugLog('useUsageTracking: Usage summary refreshed', { userId, summary });
      
    } catch (err) {
      const errorMessage = err instanceof UsageServiceError 
        ? err.message 
        : 'Failed to load usage data';
      
      debugError('useUsageTracking: Error refreshing usage', {
        userId,
        error: err
      });
      
      setError(errorMessage);
    }
  }, [userId]);

  /**
   * Load transaction history from the server
   */
  const loadTransactions = useCallback(async (limit: number = 50): Promise<void> => {
    if (!userId) {
      debugLog('useUsageTracking: No userId provided, skipping transaction load');
      return;
    }

    try {
      debugLog('useUsageTracking: Loading transaction history', { userId, limit });
      
      const history = await usageService.getCreditTransactionHistory(userId, limit);
      setTransactions(history);
      
      debugLog('useUsageTracking: Transaction history loaded', { 
        userId, 
        count: history.length 
      });
      
    } catch (err) {
      debugError('useUsageTracking: Error loading transactions', {
        userId,
        error: err
      });
      
      // Don't set error state for transaction loading failures
      // as this is secondary data
      console.warn('Failed to load transaction history:', err);
    }
  }, [userId]);

  /**
   * Load all data (usage summary and transactions)
   */
  const loadAllData = useCallback(async (): Promise<void> => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      // Load both usage summary and transactions in parallel
      await Promise.all([
        refreshUsage(),
        loadTransactions()
      ]);
    } catch (err) {
      debugError('useUsageTracking: Error loading data', { userId, error: err });
    } finally {
      setLoading(false);
    }
  }, [userId, refreshUsage, loadTransactions]);

  /**
   * Force refresh all data
   */
  const forceRefresh = useCallback(async (): Promise<void> => {
    debugLog('useUsageTracking: Force refreshing all data', { userId });
    await loadAllData();
  }, [loadAllData]);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Initial data load when userId changes
   */
  useEffect(() => {
    if (userId) {
      loadAllData();
    } else {
      // Reset state when no user
      setUsageSummary(null);
      setTransactions([]);
      setLoading(false);
      setError(null);
      setLastRefresh(null);
    }
  }, [userId, loadAllData]);

  /**
   * Auto-refresh interval
   */
  useEffect(() => {
    if (!config.autoRefresh || !userId || !config.refreshInterval) {
      return;
    }

    const interval = setInterval(() => {
      debugLog('useUsageTracking: Auto-refreshing usage data', { userId });
      refreshUsage(); // Only refresh usage summary, not transactions
    }, config.refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [config.autoRefresh, config.refreshInterval, userId, refreshUsage]);

  // ============================================================================
  // Derived State
  // ============================================================================

  /**
   * Check if user has low usage
   */
  const hasLowUsage = usageSummary ? (
    usageSummary.daily_searches_remaining <= 1 || 
    usageSummary.weekly_ai_analyses_remaining === 0
  ) : false;

  /**
   * Check if user has any credits
   */
  const hasCredits = usageSummary ? (
    usageSummary.search_credits > 0 || 
    usageSummary.ai_analysis_credits > 0
  ) : false;

  /**
   * Check if user is completely out of usage
   */
  const isOutOfUsage = usageSummary ? (
    usageSummary.daily_searches_remaining === 0 && 
    usageSummary.weekly_ai_analyses_remaining === 0 &&
    usageSummary.search_credits === 0 && 
    usageSummary.ai_analysis_credits === 0 &&
    !usageSummary.is_owner
  ) : false;

  // ============================================================================
  // Return Hook Result
  // ============================================================================

  return {
    // Core data
    usageSummary,
    transactions,
    loading,
    error,
    
    // Methods
    refreshUsage,
    loadTransactions,
    forceRefresh,
    
    // Metadata
    lastRefresh,
    
    // Derived state (useful for UI logic)
    hasLowUsage,
    hasCredits,
    isOutOfUsage,
    
    // Convenience getters
    isOwner: usageSummary?.is_owner || false,
    dailySearchesRemaining: usageSummary?.daily_searches_remaining || 0,
    weeklyAnalysesRemaining: usageSummary?.weekly_ai_analyses_remaining || 0,
    searchCredits: usageSummary?.search_credits || 0,
    aiAnalysisCredits: usageSummary?.ai_analysis_credits || 0
  } as UseUsageTrackingResult & {
    lastRefresh: Date | null;
    hasLowUsage: boolean;
    hasCredits: boolean;
    isOutOfUsage: boolean;
    isOwner: boolean;
    dailySearchesRemaining: number;
    weeklyAnalysesRemaining: number;
    searchCredits: number;
    aiAnalysisCredits: number;
    forceRefresh: () => Promise<void>;
  };
}

/**
 * Lightweight hook for just checking usage capabilities
 * Useful when you only need to know if an operation can be performed
 */
export function useUsageCheck(userId: string) {
  const [checking, setChecking] = useState(false);

  const checkOperation = useCallback(async (operationType: 'search' | 'ai_analysis') => {
    if (!userId) return { canPerform: false, reason: 'No user ID' };

    setChecking(true);
    try {
      const result = await usageService.canPerformOperation(userId, operationType);
      return result;
    } catch (error) {
      debugError('useUsageCheck: Error checking operation', { userId, operationType, error });
      return { canPerform: false, reason: 'Unable to check usage limits' };
    } finally {
      setChecking(false);
    }
  }, [userId]);

  return {
    checkOperation,
    checking
  };
}

/**
 * Hook for managing credit packages and purchases
 */
export function useCreditPackages() {
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      const creditPackages = await usageService.getCreditPackages();
      setPackages(creditPackages);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof UsageServiceError 
        ? err.message 
        : 'Failed to load credit packages';
      setError(errorMessage);
      debugError('useCreditPackages: Error loading packages', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  return {
    packages,
    loading,
    error,
    refresh: loadPackages
  };
}